/**
 * Module Schema Registry
 * 
 * Central registry for field schemas, synonyms, and target fields per module.
 * Used by both frontend (FieldMappingPrecheckModal) and edge functions.
 */

export interface TargetField {
  key: string;
  label: string;
  required: boolean;
  recommended?: boolean;
}

export interface ModuleSchema {
  moduleId: string;
  /** Human-readable name */
  name: string;
  /** Minimum required fields for module to work */
  requiredFields: string[];
  /** Optional fields that enhance analysis */
  optionalFields: string[];
  /** Rules and their field requirements */
  rulesRequirements: Record<string, string[]>;
  /** Field name synonyms for automatic mapping */
  fieldSynonyms: Record<string, string[]>;
  /** Target fields for UI display */
  targetFields: TargetField[];
}

/**
 * Shared field synonyms used across multiple modules
 */
const COMMON_FIELD_SYNONYMS = {
  Datum: [
    "datum", "date", "bokföringsdag", "bokforingsdag", "transaktionsdatum",
    "transaction_date", "bokförd", "bokford", "posting_date"
  ],
  Belopp: [
    "belopp", "amount", "summa", "sum", "belopp_sek", "amount_sek",
    "kronor", "kr", "värde", "varde", "value"
  ],
  Förfallodatum: [
    "förfallodatum", "forfallodatum", "due_date", "duedate", "förfall",
    "forfall", "due", "deadline", "förf.datum", "förf_datum"
  ],
};

/**
 * Central registry of all module schemas
 */
export const MODULE_SCHEMAS: Record<string, ModuleSchema> = {
  // ============================================================
  // Module 0: Datakvalitet / Data Quality
  // ============================================================
  data_quality: {
    moduleId: 'data_quality',
    name: 'Datakvalitet',
    requiredFields: ['Konto', 'Belopp'],
    optionalFields: ['Datum', 'Verifikationsnummer', 'Text', 'Debet', 'Kredit'],
    rulesRequirements: {
      'Tomma celler': ['Konto', 'Belopp'],
      'Ogiltiga format': ['Datum', 'Belopp'],
      'Saknade konton': ['Konto'],
    },
    fieldSynonyms: {
      ...COMMON_FIELD_SYNONYMS,
      Konto: [
        "konto", "kontonummer", "account", "account_number", "kto", "kontonr",
        "account_no", "gl_account", "bokföringskonto"
      ],
      Verifikationsnummer: [
        "verifikationsnummer", "verifikatnr", "ver_nr", "voucher_no", "voucher",
        "ver", "verifikation", "verification"
      ],
      Text: [
        "text", "beskrivning", "description", "kommentar", "comment", "note"
      ],
      Debet: [
        "debet", "debit", "deb", "d"
      ],
      Kredit: [
        "kredit", "credit", "kred", "k", "cr"
      ],
    },
    targetFields: [
      { key: "Konto", label: "Konto", required: true },
      { key: "Belopp", label: "Belopp", required: true },
      { key: "Datum", label: "Datum", required: false, recommended: true },
      { key: "Verifikationsnummer", label: "Verifikationsnummer", required: false },
      { key: "Text", label: "Text/Beskrivning", required: false },
      { key: "Debet", label: "Debet", required: false },
      { key: "Kredit", label: "Kredit", required: false },
    ],
  },

  // ============================================================
  // Module 1: Manuella verifikationer / Journal Entries
  // ============================================================
  journal_entries: {
    moduleId: 'journal_entries',
    name: 'Manuella verifikationer',
    requiredFields: ['Datum', 'Verifikationsnummer', 'Belopp', 'Konto'],
    optionalFields: ['Text', 'Användare', 'Bokföringsdatum', 'Typ'],
    rulesRequirements: {
      'Bokslutsnära justeringar': ['Datum', 'Belopp'],
      'Rundade belopp': ['Belopp'],
      'Management override': ['Användare', 'Belopp'],
      'Ovanliga kontokombinationer': ['Konto'],
    },
    fieldSynonyms: {
      ...COMMON_FIELD_SYNONYMS,
      Verifikationsnummer: [
        "verifikationsnummer", "verifikatnr", "ver_nr", "voucher_no", "voucher",
        "ver", "verifikation", "verification", "journal_id"
      ],
      Konto: [
        "konto", "kontonummer", "account", "account_number", "kto", "kontonr"
      ],
      Text: [
        "text", "beskrivning", "description", "kommentar", "comment"
      ],
      Användare: [
        "användare", "anvandare", "user", "created_by", "bokförd_av", "registrerad_av"
      ],
      Bokföringsdatum: [
        "bokföringsdatum", "bokforingsdatum", "posting_date", "registrerad"
      ],
      Typ: [
        "typ", "type", "journal_type", "verifikationstyp"
      ],
    },
    targetFields: [
      { key: "Datum", label: "Datum", required: true },
      { key: "Verifikationsnummer", label: "Verifikationsnummer", required: true },
      { key: "Belopp", label: "Belopp", required: true },
      { key: "Konto", label: "Konto", required: true },
      { key: "Text", label: "Text/Beskrivning", required: false, recommended: true },
      { key: "Användare", label: "Användare", required: false },
      { key: "Bokföringsdatum", label: "Bokföringsdatum", required: false },
      { key: "Typ", label: "Typ", required: false },
    ],
  },

  // ============================================================
  // Module 2: Leverantörsskulder / Accounts Payable
  // ============================================================
  accounts_payable: {
    moduleId: 'accounts_payable',
    name: 'Leverantörsskulder',
    requiredFields: ['Fakturanummer', 'Leverantör', 'Belopp', 'Förfallodatum'],
    optionalFields: ['Fakturadatum', 'Betalstatus', 'Betaldatum', 'Valuta', 'Moms'],
    rulesRequirements: {
      'Förfallna skulder': ['Förfallodatum', 'Betalstatus'],
      'Dubblettfakturor': ['Fakturanummer', 'Leverantör', 'Belopp'],
      'Koncentrerad leverantör': ['Leverantör', 'Belopp'],
      'Ovanliga belopp': ['Belopp'],
    },
    fieldSynonyms: {
      ...COMMON_FIELD_SYNONYMS,
      Fakturanummer: [
        "fakturanummer", "fakturanr", "invoice_number", "invoice_no", "inv_no",
        "invoiceid", "faktura_id", "leverantörsfaktura"
      ],
      Leverantör: [
        "leverantör", "leverantor", "supplier", "vendor", "leverantörsnamn",
        "supplier_name", "vendor_name"
      ],
      Fakturadatum: [
        "fakturadatum", "invoice_date", "invoicedate", "datum"
      ],
      Betalstatus: [
        "betalstatus", "payment_status", "status", "betald", "paid"
      ],
      Betaldatum: [
        "betaldatum", "payment_date", "paid_date", "betalningsdatum"
      ],
      Valuta: [
        "valuta", "currency", "valutakod", "curr"
      ],
      Moms: [
        "moms", "vat", "mervärdesskatt", "momssats", "vat_amount"
      ],
    },
    targetFields: [
      { key: "Fakturanummer", label: "Fakturanummer", required: true },
      { key: "Leverantör", label: "Leverantör", required: true },
      { key: "Belopp", label: "Belopp", required: true },
      { key: "Förfallodatum", label: "Förfallodatum", required: true },
      { key: "Fakturadatum", label: "Fakturadatum", required: false, recommended: true },
      { key: "Betalstatus", label: "Betalstatus", required: false },
      { key: "Betaldatum", label: "Betaldatum", required: false },
      { key: "Valuta", label: "Valuta", required: false },
      { key: "Moms", label: "Moms", required: false },
    ],
  },

  // ============================================================
  // Module 3: Kundfordringar / Accounts Receivable
  // ============================================================
  accounts_receivable: {
    moduleId: 'accounts_receivable',
    name: 'Kundfordringar',
    requiredFields: ['Fakturanummer', 'Kund', 'Belopp', 'Förfallodatum'],
    optionalFields: ['Fakturadatum', 'Betalstatus', 'Betaldatum', 'Valuta', 'Moms'],
    rulesRequirements: {
      'Förfallna fordringar': ['Förfallodatum', 'Betalstatus'],
      'Kreditrisk': ['Kund', 'Belopp', 'Förfallodatum'],
      'Koncentrerad kund': ['Kund', 'Belopp'],
      'Potentiella nedskrivningar': ['Förfallodatum', 'Belopp'],
    },
    fieldSynonyms: {
      ...COMMON_FIELD_SYNONYMS,
      Fakturanummer: [
        "fakturanummer", "fakturanr", "invoice_number", "invoice_no", "inv_no",
        "invoiceid", "faktura_id"
      ],
      Kund: [
        "kund", "customer", "client", "kundnamn", "customer_name", "köpare",
        "motpart", "mottagare"
      ],
      Fakturadatum: [
        "fakturadatum", "invoice_date", "invoicedate", "datum"
      ],
      Betalstatus: [
        "betalstatus", "payment_status", "status", "betald", "paid"
      ],
      Betaldatum: [
        "betaldatum", "payment_date", "paid_date", "betalningsdatum"
      ],
      Valuta: [
        "valuta", "currency", "valutakod", "curr"
      ],
      Moms: [
        "moms", "vat", "mervärdesskatt", "momssats", "vat_amount"
      ],
    },
    targetFields: [
      { key: "Fakturanummer", label: "Fakturanummer", required: true },
      { key: "Kund", label: "Kund", required: true },
      { key: "Belopp", label: "Belopp", required: true },
      { key: "Förfallodatum", label: "Förfallodatum", required: true },
      { key: "Fakturadatum", label: "Fakturadatum", required: false, recommended: true },
      { key: "Betalstatus", label: "Betalstatus", required: false },
      { key: "Betaldatum", label: "Betaldatum", required: false },
      { key: "Valuta", label: "Valuta", required: false },
      { key: "Moms", label: "Moms", required: false },
    ],
  },

  // ============================================================
  // Module 4: Bank och kassa / Bank & Cash
  // ============================================================
  bank_cash: {
    moduleId: 'bank_cash',
    name: 'Bank och kassa',
    requiredFields: ['Datum', 'Belopp', 'Text'],
    optionalFields: ['Saldo', 'Referens', 'Kategori', 'Verifikationsnummer', 'Konto'],
    rulesRequirements: {
      'Dubbletter': ['Datum', 'Belopp', 'Text'],
      'Oregelbundna transaktioner': ['Datum', 'Belopp'],
      'Stora uttag': ['Belopp', 'Text'],
      'Avstämningsavvikelser': ['Saldo'],
    },
    fieldSynonyms: {
      ...COMMON_FIELD_SYNONYMS,
      Text: [
        "text", "beskrivning", "description", "meddelande", "message",
        "transaktion", "transaction", "info"
      ],
      Referens: [
        "referens", "reference", "ref", "ocr", "ocr-nummer", "betalningsreferens"
      ],
      Kategori: [
        "kategori", "category", "typ", "type", "kontotyp"
      ],
      Saldo: [
        "saldo", "balance", "balans", "kvarvarande", "remaining"
      ],
      Verifikationsnummer: [
        "verifikationsnummer", "verifikatnr", "ver_nr", "voucher_no"
      ],
      Konto: [
        "konto", "kontonummer", "account", "account_number", "bankkonto"
      ],
    },
    targetFields: [
      { key: "Datum", label: "Datum", required: true },
      { key: "Belopp", label: "Belopp", required: true },
      { key: "Text", label: "Text/Beskrivning", required: true },
      { key: "Saldo", label: "Saldo", required: false, recommended: true },
      { key: "Referens", label: "Referens/OCR", required: false },
      { key: "Kategori", label: "Kategori", required: false },
      { key: "Verifikationsnummer", label: "Verifikationsnummer", required: false },
      { key: "Konto", label: "Konto", required: false },
    ],
  },

  // ============================================================
  // Module 5: Skattekonto / Tax Account (existing)
  // ============================================================
  taxes_fees: {
    moduleId: 'taxes_fees',
    name: 'Skattekonto',
    requiredFields: ['Datum', 'Händelse', 'Belopp'],
    optionalFields: ['Saldo', 'Förfallodatum', 'TransaktionsID', 'DeklareratBelopp'],
    rulesRequirements: {
      'Försenad inbetalning': ['Förfallodatum'],
      'Deklarerat vs bokfört': ['DeklareratBelopp'],
      'Avgift/ränta utan skuld': ['Händelse'],
      'Oregelbunden preliminärskatt': ['Händelse'],
    },
    fieldSynonyms: {
      ...COMMON_FIELD_SYNONYMS,
      Händelse: [
        "händelse", "handelse", "event", "typ", "type", "transaction_type",
        "text", "beskrivning", "description", "transaktion", "transaction"
      ],
      Saldo: [
        "saldo", "balance", "balans", "kvarvarande", "remaining", "skuld", "debt", "total"
      ],
      DeklareratBelopp: [
        "deklarerat", "declared", "deklarerat_belopp", "declared_amount",
        "ska_betalas", "expected", "förväntat", "forvantat", "budgeterat"
      ],
      TransaktionsID: [
        "transaktionsid", "transaction_id", "trans_id", "id", "unique_id",
        "rad_id", "row_id", "nummer", "number"
      ],
    },
    targetFields: [
      { key: "Datum", label: "Datum", required: true },
      { key: "Händelse", label: "Händelse", required: true },
      { key: "Belopp", label: "Belopp", required: true },
      { key: "Saldo", label: "Saldo", required: false, recommended: true },
      { key: "Förfallodatum", label: "Förfallodatum", required: false },
      { key: "TransaktionsID", label: "TransaktionsID", required: false },
      { key: "DeklareratBelopp", label: "Deklarerat Belopp", required: false },
    ],
  },

  // ============================================================
  // Module 6: Moms / VAT
  // ============================================================
  vat: {
    moduleId: 'vat',
    name: 'Momsanalys',
    requiredFields: ['Period', 'IngåendeMoms', 'UtgåendeMoms'],
    optionalFields: ['Differens', 'Belopp', 'Datum', 'Beskrivning', 'Konto'],
    rulesRequirements: {
      'Momsavstämning': ['IngåendeMoms', 'UtgåendeMoms'],
      'Felaktig momssats': ['Belopp', 'UtgåendeMoms'],
      'Periodisering': ['Period', 'Datum'],
    },
    fieldSynonyms: {
      ...COMMON_FIELD_SYNONYMS,
      Period: [
        "period", "momsperiod", "rapporteringsperiod", "vat_period", "månad"
      ],
      IngåendeMoms: [
        "ingående", "ingaende", "ingående_moms", "input_vat", "förvärvsmoms",
        "deductible_vat", "ing_moms"
      ],
      UtgåendeMoms: [
        "utgående", "utgaende", "utgående_moms", "output_vat", "säljmoms",
        "collected_vat", "utg_moms"
      ],
      Differens: [
        "differens", "diff", "skillnad", "difference", "momsatt_betala"
      ],
      Beskrivning: [
        "beskrivning", "description", "text", "kommentar"
      ],
      Konto: [
        "konto", "kontonummer", "account", "momskonto"
      ],
    },
    targetFields: [
      { key: "Period", label: "Period", required: true },
      { key: "IngåendeMoms", label: "Ingående moms", required: true },
      { key: "UtgåendeMoms", label: "Utgående moms", required: true },
      { key: "Differens", label: "Differens", required: false, recommended: true },
      { key: "Belopp", label: "Belopp", required: false },
      { key: "Datum", label: "Datum", required: false },
      { key: "Beskrivning", label: "Beskrivning", required: false },
      { key: "Konto", label: "Konto", required: false },
    ],
  },

  // ============================================================
  // Module 7: Dubletter och avvikelser / Duplicates & Anomalies
  // ============================================================
  duplicates_anomalies: {
    moduleId: 'duplicates_anomalies',
    name: 'Dubletter och avvikelser',
    requiredFields: ['Fakturanummer', 'Belopp', 'Datum'],
    optionalFields: ['Leverantör', 'Kund', 'Text', 'Verifikationsnummer'],
    rulesRequirements: {
      'Dubblettfakturor': ['Fakturanummer', 'Belopp', 'Datum'],
      'Beloppsavvikelser': ['Belopp'],
      'Datumavvikelser': ['Datum'],
    },
    fieldSynonyms: {
      ...COMMON_FIELD_SYNONYMS,
      Fakturanummer: [
        "fakturanummer", "fakturanr", "invoice_number", "invoice_no", "inv_no",
        "invoiceid", "faktura_id"
      ],
      Leverantör: [
        "leverantör", "leverantor", "supplier", "vendor", "leverantörsnamn"
      ],
      Kund: [
        "kund", "customer", "client", "kundnamn", "customer_name"
      ],
      Text: [
        "text", "beskrivning", "description", "kommentar"
      ],
      Verifikationsnummer: [
        "verifikationsnummer", "verifikatnr", "ver_nr", "voucher_no"
      ],
    },
    targetFields: [
      { key: "Fakturanummer", label: "Fakturanummer", required: true },
      { key: "Belopp", label: "Belopp", required: true },
      { key: "Datum", label: "Datum", required: true },
      { key: "Leverantör", label: "Leverantör", required: false, recommended: true },
      { key: "Kund", label: "Kund", required: false },
      { key: "Text", label: "Text/Beskrivning", required: false },
      { key: "Verifikationsnummer", label: "Verifikationsnummer", required: false },
    ],
  },

  // ============================================================
  // Revenue (existing - alias for module_6 in some contexts)
  // ============================================================
  revenue: {
    moduleId: 'revenue',
    name: 'Intäktsanalys',
    requiredFields: ['Fakturanummer', 'Fakturadatum', 'Belopp', 'Kund'],
    optionalFields: ['Förfallodatum', 'Moms', 'Betalstatus', 'Betaldatum', 'Valuta'],
    rulesRequirements: {
      'Förfallen faktura': ['Förfallodatum'],
      'Momsvalidering': ['Moms', 'Belopp'],
      'Betalningsstatus': ['Betalstatus', 'Betaldatum'],
    },
    fieldSynonyms: {
      ...COMMON_FIELD_SYNONYMS,
      Fakturanummer: [
        "fakturanummer", "fakturanr", "invoice_number", "invoice_no", "inv_no",
        "invoiceid", "faktura_id", "verifikationsnummer"
      ],
      Fakturadatum: [
        "fakturadatum", "invoice_date", "invoicedate", "datum", "date"
      ],
      Kund: [
        "kund", "customer", "client", "kundnamn", "customer_name", "köpare",
        "motpart", "mottagare"
      ],
      Moms: [
        "moms", "vat", "mervärdesskatt", "momssats", "vat_amount", "tax"
      ],
      Betalstatus: [
        "betalstatus", "payment_status", "status", "betald", "paid", "obetald"
      ],
      Betaldatum: [
        "betaldatum", "payment_date", "paid_date", "betalningsdatum"
      ],
      Valuta: [
        "valuta", "currency", "valutakod", "curr"
      ],
    },
    targetFields: [
      { key: "Fakturanummer", label: "Fakturanummer", required: true },
      { key: "Fakturadatum", label: "Fakturadatum", required: true },
      { key: "Belopp", label: "Belopp", required: true },
      { key: "Kund", label: "Kund", required: true },
      { key: "Förfallodatum", label: "Förfallodatum", required: false, recommended: true },
      { key: "Moms", label: "Moms", required: false },
      { key: "Betalstatus", label: "Betalstatus", required: false },
      { key: "Betaldatum", label: "Betaldatum", required: false },
      { key: "Valuta", label: "Valuta", required: false },
    ],
  },

  // ============================================================
  // Bank Reconciliation (existing - subset of bank_cash)
  // ============================================================
  bank_reconciliation: {
    moduleId: 'bank_reconciliation',
    name: 'Bankavstämning',
    requiredFields: ['Datum', 'Belopp', 'Text'],
    optionalFields: ['Saldo', 'Referens', 'Kategori', 'Verifikationsnummer'],
    rulesRequirements: {
      'Dubblettransaktioner': ['Datum', 'Belopp', 'Text'],
      'Oregelbundna mönster': ['Datum', 'Belopp'],
    },
    fieldSynonyms: {
      ...COMMON_FIELD_SYNONYMS,
      Text: [
        "text", "beskrivning", "description", "meddelande", "message",
        "transaktion", "transaction", "info"
      ],
      Referens: [
        "referens", "reference", "ref", "ocr", "ocr-nummer", "betalningsreferens"
      ],
      Kategori: [
        "kategori", "category", "typ", "type", "kontotyp"
      ],
      Saldo: [
        "saldo", "balance", "balans", "kvarvarande", "remaining"
      ],
      Verifikationsnummer: [
        "verifikationsnummer", "verifikatnr", "ver_nr", "voucher_no"
      ],
    },
    targetFields: [
      { key: "Datum", label: "Datum", required: true },
      { key: "Belopp", label: "Belopp", required: true },
      { key: "Text", label: "Text/Beskrivning", required: true },
      { key: "Saldo", label: "Saldo", required: false, recommended: true },
      { key: "Referens", label: "Referens/OCR", required: false },
      { key: "Kategori", label: "Kategori", required: false },
      { key: "Verifikationsnummer", label: "Verifikationsnummer", required: false },
    ],
  },
};

import { normalizeModuleId } from './module-registry';

/**
 * Get schema for a module, handling legacy IDs via normalization
 */
export function getModuleSchema(moduleId: string): ModuleSchema {
  // Normalize to semantic ID (logs warning for legacy IDs in dev)
  const normalizedId = normalizeModuleId(moduleId);
  
  if (MODULE_SCHEMAS[normalizedId]) {
    return MODULE_SCHEMAS[normalizedId];
  }
  
  console.warn(`[schema-registry] Unknown moduleId: ${normalizedId}, defaulting to taxes_fees`);
  return MODULE_SCHEMAS.taxes_fees;
}

/**
 * Get all registered module IDs (excluding aliases)
 */
export function getAllModuleSchemaIds(): string[] {
  return Object.keys(MODULE_SCHEMAS);
}

/**
 * Check if a module schema exists
 */
export function hasModuleSchema(moduleId: string): boolean {
  const normalizedId = normalizeModuleId(moduleId);
  return !!MODULE_SCHEMAS[normalizedId];
}

/**
 * Get target fields for a module
 */
export function getTargetFields(moduleId: string): TargetField[] {
  return getModuleSchema(moduleId).targetFields;
}

/**
 * Get required fields for a module
 */
export function getRequiredFields(moduleId: string): string[] {
  return getModuleSchema(moduleId).requiredFields;
}

/**
 * Get field synonyms for a module
 */
export function getFieldSynonyms(moduleId: string): Record<string, string[]> {
  return getModuleSchema(moduleId).fieldSynonyms;
}
